/**
 * ErrorPage Component
 *
 * Displayed when session initialization fails or a fatal error occurs
 * before the main application can mount.
 */

import { maskCredential } from '@utils/maskCredential';

/**
 * Props for ErrorPage component
 */
export interface ErrorPageProps {
  /** Page title */
  title: string;

  /** Error message to display */
  message: string;

  /** Session ID for support reference (first 8 chars will be shown) */
  sessionId?: string;

  /** Button label — defaults to "Reload Page" */
  actionLabel?: string;

  /** Button action — defaults to window.location.reload() */
  onAction?: () => void;
}

/**
 * ErrorPage
 *
 * Renders a full-page error state with:
 * - Configurable title and message
 * - Recovery instructions
 * - Optional masked session ID for support reference
 */
export function ErrorPage({
  title,
  message,
  sessionId,
  actionLabel,
  onAction,
}: ErrorPageProps) {
  return (
    <div
      data-testid="error-page"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        textAlign: 'center',
        fontFamily: 'sans-serif',
        backgroundColor: '#f9fafb',
        color: '#1a1a1a',
      }}
    >
      <div
        style={{
          maxWidth: '480px',
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '2.5rem 2rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
          }}
          aria-hidden="true"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h1
          style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '0.75rem',
            color: '#111827',
          }}
        >
          {title}
        </h1>

        <p
          style={{
            fontSize: '0.9375rem',
            color: '#6b7280',
            marginBottom: '1rem',
            lineHeight: '1.5',
          }}
        >
          {message}
        </p>

        <p
          style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '1.5rem',
          }}
        >
          Please reload the page or contact support.
        </p>

        <button
          type="button"
          data-testid="error-page-reload-button"
          onClick={onAction ?? (() => window.location.reload())}
          style={{
            display: 'block',
            width: '100%',
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            fontWeight: '500',
            color: '#ffffff',
            backgroundColor: '#06a4f3',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: sessionId ? '1.5rem' : '0',
          }}
        >
          {actionLabel ?? 'Reload Page'}
        </button>

        {sessionId && (
          <p
            data-testid="error-page-session-id"
            style={{
              fontSize: '0.8125rem',
              color: '#9ca3af',
              fontFamily: 'monospace',
              backgroundColor: '#f3f4f6',
              padding: '0.5rem 0.75rem',
              borderRadius: '4px',
              marginTop: '1rem',
            }}
          >
            Session reference: {maskCredential(sessionId)}
          </p>
        )}
      </div>
    </div>
  );
}
